import {
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';

/** Floor, so a fast load reads as a deliberate transition rather than a flicker. */
const MIN_VISIBLE_MS = 300;
/** Ceiling, so a stalled or failed fetch reveals the screen instead of trapping the user. */
const MAX_HOLD_MS = 5000;
/**
 * A screen that has not reported by this point is treated as ready. Covers both
 * screens with no async work and screens not yet wired to the hook, and must
 * outlast the outgoing screen's exit, since `mode="wait"` delays the
 * destination's mount (and therefore its first report) until the exit finishes.
 */
const REPORT_GRACE_MS = 350;

/** Routes that do no async work on mount: they cross-fade with no loader. */
const INSTANT_PATHS = new Set([
  '/',
  '/onboarding',
  '/login',
  '/signup',
  '/setup',
  '/settings',
  '/safety',
  '/safety/muted',
  '/explore/history',
  '/country-selection',
  '/profile/edit',
  '/profile/flags',
  // Fetches only on submit, so there is nothing to wait for on mount.
  '/report',
]);

/** A friend's yaps render from already-loaded data, unlike their profile. */
const FRIEND_YAPS_PATH = /^\/profile\/[^/]+\/yaps$/;

function isInstant(path: string): boolean {
  if (INSTANT_PATHS.has(path) || path.startsWith('/legal/')) return true;
  // The event itself arrives in router state; only its comments stream in.
  if (path.startsWith('/event/')) return true;
  return FRIEND_YAPS_PATH.test(path);
}

/**
 * Two URLs that are one screen. `/yaptab` and `/yaptab/friends` are the same
 * component driven by the same tab state, and `/discover/:country` is Discover
 * with a filter applied. Both are route changes, but both read as in-screen tab
 * switches, and a full-screen loader on a tab tap is worse than no loader.
 */
function isSameScreen(from: string, to: string): boolean {
  const collapse = (p: string) =>
    p.startsWith('/yaptab') ? '/yaptab' : p.startsWith('/discover') ? '/discover' : p;
  return collapse(from) === collapse(to);
}

interface ReadinessBus {
  report: (id: string, isReady: boolean) => void;
  unregister: (id: string) => void;
}

const ReadinessContext = createContext<ReadinessBus | null>(null);
const LoaderVisibleContext = createContext(false);

/**
 * Reports whether this screen's primary content has loaded. Screens pass the
 * loading flag they already compute; the overlay holds until every reporter
 * under the current route says it is ready.
 *
 * Readiness is tracked per caller rather than last-write-wins because routes
 * can nest reporters: /discover renders a selector that resolves a feature flag
 * and then mounts the Discover screen, which does its own fetching. With a
 * single shared flag the selector's "ready" would cancel the screen's "loading".
 */
export function useScreenReady(isReady: boolean) {
  const bus = useContext(ReadinessContext);
  const id = useId();

  useEffect(() => {
    if (!bus) return;
    bus.report(id, isReady);
    return () => bus.unregister(id);
  }, [bus, id, isReady]);
}

/** True while the transition overlay is covering the screen. */
export function useTransitionLoaderVisible(): boolean {
  return useContext(LoaderVisibleContext);
}

export function ScreenTransitionProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [holding, setHolding] = useState(false);
  const [ready, setReady] = useState(true);
  const previousPathRef = useRef(pathname);
  const startedAtRef = useRef(0);
  const readinessRef = useRef(new Map<string, boolean>());

  // Start the hold before paint, so the overlay covers the destination's first
  // frame rather than appearing over content the user has already seen.
  useLayoutEffect(() => {
    const previous = previousPathRef.current;
    if (previous === pathname) return;
    previousPathRef.current = pathname;
    if (isInstant(pathname) || isSameScreen(previous, pathname)) return;

    // The outgoing screen's entries describe a screen that is on its way out.
    readinessRef.current.clear();
    startedAtRef.current = Date.now();
    setReady(false);
    setHolding(true);
  }, [pathname]);

  useEffect(() => {
    if (!holding) return;
    const elapsed = () => Date.now() - startedAtRef.current;

    const ceiling = window.setTimeout(
      () => setHolding(false),
      Math.max(0, MAX_HOLD_MS - elapsed()),
    );
    // If nothing is registered by now, nothing is going to hold the overlay:
    // the destination either does no async work or is not wired to the hook.
    // Asking the map rather than "has anyone ever reported" keeps the outgoing
    // screen, which reports again while it exits, from answering for the
    // incoming one.
    const grace = window.setTimeout(() => {
      if (readinessRef.current.size === 0) setReady(true);
    }, REPORT_GRACE_MS);

    return () => {
      clearTimeout(ceiling);
      clearTimeout(grace);
    };
  }, [holding]);

  useEffect(() => {
    if (!holding || !ready) return;
    const remaining = Math.max(0, MIN_VISIBLE_MS - (Date.now() - startedAtRef.current));
    const timer = window.setTimeout(() => setHolding(false), remaining);
    return () => clearTimeout(timer);
  }, [holding, ready]);

  // Stable identity: screens depend on this in an effect, and a new object each
  // render would make every screen re-report on every parent render.
  const bus = useMemo<ReadinessBus>(
    () => ({
      report: (id, isReady) => {
        const readiness = readinessRef.current;
        readiness.set(id, isReady);
        setReady(Array.from(readiness.values()).every(Boolean));
      },
      // The outgoing screen keeps reporting while it plays its exit, so it can
      // leave a stale "not ready" behind after the map was cleared for the new
      // route. Recomputing as it unmounts clears that; without it the overlay
      // sat until the ceiling on every navigation. An emptied map is left to
      // the grace timer rather than treated as ready here, since the
      // destination may not have mounted yet.
      unregister: (id) => {
        const readiness = readinessRef.current;
        readiness.delete(id);
        if (readiness.size > 0) setReady(Array.from(readiness.values()).every(Boolean));
      },
    }),
    [],
  );

  return (
    <ReadinessContext.Provider value={bus}>
      <LoaderVisibleContext.Provider value={holding}>
        {children}
      </LoaderVisibleContext.Provider>
    </ReadinessContext.Provider>
  );
}
